export type ChatRecipient = 'sara' | 'sales'

/** Sticky routing — `sara_private` keeps follow-ups private to the customer. */
export type ChatStickyRecipient = 'sara' | 'sara_private' | null

export type DetectChatRecipientResult = {
  recipient: ChatRecipient
  nextSticky: ChatStickyRecipient
  cleanedText: string
  isPrivate: boolean
}

const EXPLICIT_SARA_PATTERNS = [
  /(?:^|\s)@sara\b/i,
  /^sara[,\s]/i,
  /^sara\s+help\s+me\b/i,
  /^sara\s+could\s+you\b/i,
]

/** Private Sara invoke — underscore is a word char, so @sara\b / "Sara " miss it. */
const SARA_PRIVATE_PREFIX = /^sara_/i

const EXPLICIT_TEAM_PATTERNS = [
  /\bhey\s+team\b/i,
  /\bteam\s+klayder\b/i,
  /\bklayder\s+team\b/i,
  /\b(?:talk|speak)\s+(?:to|with)\s+(?:the\s+)?team\b/i,
  /\b(?:talk|speak)\s+(?:to|with)\s+someone\b/i,
  /\breal\s+person\b/i,
  /(?:^|\s)@team\b/i,
]

const normalizeSaraPrivatePrefix = (text: string) =>
  text.replace(SARA_PRIVATE_PREFIX, 'Sara ').replace(/\s+/g, ' ').trim()

/**
 * After Team Klayder speaks, plain follow-ups must go to the team until the
 * user explicitly @Sara again — otherwise sticky routing steals replies like "Ja!".
 */
export const resolveStickyRecipient = (
  stickyRecipient: ChatStickyRecipient,
  latestTeamMessageAt: number | null,
  latestExplicitSaraUserAt: number | null
): ChatStickyRecipient => {
  if (!stickyRecipient) return null

  if (
    latestTeamMessageAt != null &&
    (latestExplicitSaraUserAt == null ||
      latestTeamMessageAt > latestExplicitSaraUserAt)
  ) {
    return null
  }

  return stickyRecipient
}

export const detectChatRecipient = (
  plainText: string,
  stickyRecipient: ChatStickyRecipient
): DetectChatRecipientResult => {
  const trimmed = plainText.trim()

  if (!trimmed) {
    return {
      recipient: stickyRecipient ? 'sara' : 'sales',
      nextSticky: stickyRecipient,
      cleanedText: trimmed,
      isPrivate: stickyRecipient === 'sara_private',
    }
  }

  if (SARA_PRIVATE_PREFIX.test(trimmed)) {
    return {
      recipient: 'sara',
      nextSticky: 'sara_private',
      cleanedText: normalizeSaraPrivatePrefix(trimmed),
      isPrivate: true,
    }
  }

  const isExplicitSara = EXPLICIT_SARA_PATTERNS.some((pattern) =>
    pattern.test(trimmed)
  )
  const isExplicitTeam = EXPLICIT_TEAM_PATTERNS.some((pattern) =>
    pattern.test(trimmed)
  )

  if (isExplicitSara) {
    return {
      recipient: 'sara',
      nextSticky: 'sara',
      // Keep "Sara" / "@Sara" in the stored message so the bubble matches what
      // the user typed; routing already used the mention patterns above.
      cleanedText: trimmed,
      isPrivate: false,
    }
  }

  if (isExplicitTeam) {
    return {
      recipient: 'sales',
      nextSticky: null,
      cleanedText: trimmed,
      isPrivate: false,
    }
  }

  if (stickyRecipient === 'sara' || stickyRecipient === 'sara_private') {
    return {
      recipient: 'sara',
      nextSticky: stickyRecipient,
      cleanedText: trimmed,
      isPrivate: stickyRecipient === 'sara_private',
    }
  }

  return {
    recipient: 'sales',
    nextSticky: null,
    cleanedText: trimmed,
    isPrivate: false,
  }
}

/** True when the text itself addresses Sara (ignores sticky routing). */
export const isExplicitSaraText = (plainText: string): boolean =>
  detectChatRecipient(plainText, null).recipient === 'sara'
